<i18n>
  {
    "fr": {
      "title": "Gestion documentaire",
      "add-document": "Ajouter un document",
      "add-document-submit-btn": "Ajouter",
      "documents-todo": "Documents à faire",
      "documents-completed": "Documents complétés",
      "documents-suggested": "Documents suggérés"
    },
    "en": {
      "title": "Document Management",
      "add-document": "Add a document",
      "add-document-submit-btn": "Add",
      "documents-todo": "Documents to do",
      "documents-completed": "Completed documents",
      "documents-suggested": "Suggested documents"
    }
  }
</i18n>

<template>
  <div>
    <prismic-content :doctype="$consts.prismic.DOCUMENT_TYPE_INDICATOR_DETAIL" v-slot="{ content }" @loaded="$emit('ready')">
      <template v-if="indicator">
        <h2 class="title is-4 has-text-weight-bold mt-0">{{ $t('title') }}</h2>

        <div v-if="$prismicHasText(indicatorContent.suggested_documents)">
          <p
            class="subtitle is-5 has-text-weight-bold"
            :class="{
              'pt-16px': $media.mobile,
              'pt-24px': $media.tablet,
              'pt-32px': $media.widescreen
            }"
          >
            {{ $t('documents-suggested') }}
          </p>
          <div class="content is-custom">
            <prismic-rich-text :field="indicatorContent.suggested_documents" />
          </div>
        </div>

        <p
          class="subtitle is-5 has-text-weight-bold is-marginless"
          :class="{
            'pt-16px': $media.mobile,
            'pt-24px': $media.tablet,
            'pt-32px': $media.widescreen
          }"
        >
          {{ $t('documents-todo') }}
        </p>
        <b-message v-if="$prismicText(content.document_list_message)" type="is-warning" class="mt-24px is-size-7">
          <prismic-rich-text :field="content.document_list_message" />
        </b-message>

        <div>
          <b-button
            v-show="!addDocumentFormVisible"
            type="is-link"
            :disabled="addDocumentFormVisible || readOnly"
            icon-left="plus-circle"
            @click="toggleAddDocumentForm(true)"
            >{{ $t('add-document') }}</b-button
          >
          <document-form
            v-if="addDocumentFormVisible"
            :loading="loading"
            :title="$t('add-document')"
            :submit-label="$t('add-document-submit-btn')"
            @submit="(doc) => addDocument(doc)"
            @cancel="toggleAddDocumentForm(false)"
            class="card is-shadowless"
            style="border: 1px solid #d0d8e5"
            :class="{
              'p-12px': $media.mobile,
              'p-16px': $media.tablet,
              'p-24px': $media.desktop
            }"
          />
        </div>
      </template>

      <!-- Liste de documents -->
      <transition-group
        v-show="hasTodoDocs || hasDoneDocs"
        name="documents"
        tag="div"
        :class="{
          'mt-24px': $media.mobile,
          'mt-32px': $media.tablet,
          'mt-48px': $media.widescreen
        }"
      >
        <indicator-document
          v-for="doc in todoDocs"
          :id="`doc-${doc.id}`"
          :key="doc.id"
          :doc="doc"
          :editable="true"
          :editing.sync="doc.__editing"
          :readOnly="readOnly"
          @onChangeChecked="(ev) => toggleDocumentDone(doc, !doc.isDone)"
          @onSaveNotes="(notes) => setDocumentNotes(doc, notes)"
          @on-delete="(doc) => deleteDocument(doc)"
          @on-update="(updatedDoc) => updateDocument(doc, updatedDoc)"
          class="mt-24px"
        />
        <div key="done" style="overflow: hidden" :style="{ opacity: hasDoneDocs ? 1 : 0, height: hasDoneDocs ? 'auto' : '0' }">
          <p
            class="subtitle is-5 has-text-weight-bold is-marginless"
            :class="{
              'pt-24px': $media.mobile,
              'pt-32px': $media.tablet,
              'pt-48px': $media.widescreen
            }"
          >
            {{ $t('documents-completed') }}
          </p>
          <b-message v-if="$prismicText(content.document_list_completed_message)" type="is-warning" class="mt-24px is-size-7">
            <prismic-rich-text :field="content.document_list_completed_message" />
          </b-message>
          <b-message
            v-if="hasAnyMissingLink && $prismicText(content.document_list_missing_link_message)"
            type="is-danger"
            class="mt-24px is-size-7"
          >
            <prismic-rich-text :field="content.document_list_missing_link_message" />
          </b-message>
        </div>
        <draggable key="draggable" :value="doneDocs" @change="handleSortDocument" handle="[data-draghandle]">
          <indicator-document
            v-for="doc in doneDocs"
            :id="`doc-${doc.id}`"
            :key="doc.id"
            :doc="doc"
            :editable="true"
            :editing.sync="doc.__editing"
            :drag-handle="true"
            :readOnly="readOnly"
            @onChangeChecked="(ev) => toggleDocumentDone(doc, !doc.isDone)"
            @onSaveNotes="(notes) => setDocumentNotes(doc, notes)"
            @on-delete="(doc) => deleteDocument(doc)"
            @on-update="(updatedDoc) => updateDocument(doc, updatedDoc)"
            class="mt-24px"
          />
        </draggable>
      </transition-group>
    </prismic-content>
  </div>
</template>

<script>
import i18n from '@/helpers/i18n'
import draggable from 'vuedraggable'
import IndicatorDocument from '@/components/indicator/document'
import DocumentForm from '@/components/indicator/document-form'
import PrismicContent from '@/components/prismic/prismic-content'

export default {
  components: {
    draggable,
    IndicatorDocument,
    DocumentForm,
    PrismicContent
  },
  props: {
    indicator: Object,
    indicatorContent: Object,
    readOnly: Boolean
  },
  data() {
    return {
      loading: false,
      addDocumentFormVisible: false,
      addDocumentFormModel: null
    }
  },
  computed: {
    todoDocs() {
      if (this.indicator == null) return []
      return this.indicator.documents.filter((x) => x.isDone === false).sort((a, b) => a.sortOrder - b.sortOrder)
    },
    hasTodoDocs() {
      return this.todoDocs && this.todoDocs.length > 0
    },
    doneDocs() {
      if (this.indicator == null) return []
      return this.indicator.documents.filter((x) => x.isDone === true).sort((a, b) => a.sortOrder - b.sortOrder)
    },
    hasDoneDocs() {
      return this.doneDocs && this.doneDocs.length > 0
    },
    hasAnyMissingLink() {
      return this.doneDocs.some((x) => !x.link)
    }
  },
  methods: {
    toggleAddDocumentForm(show) {
      if (show) {
        this.addDocumentFormVisible = true
        this.addDocumentFormModel = {
          title: '',
          link: '',
          content: ''
        }
      } else {
        this.addDocumentFormVisible = false
        this.addDocumentFormModel = null
      }
    },
    async addDocument(addDocumentFormModel) {
      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: this.$withFragments(
            this.$options.mutation.AddUserDocument,
            this.$options.fragments.DocumentListFields,
            this.$options.fragments.IndicatorFields,
            this.$options.fragments.ProjectFields,
            this.$options.fragments.UpdatedAtDocumentFields
          ),
          variables: {
            input: {
              indicatorId: this.indicator.id,
              language: i18n.contentLanguage(),
              ...addDocumentFormModel
            }
          },
          update: (cache, { data }) => {
            this.$emit('documentsMutated', {
              cache: cache,
              action: 'added',
              data: data.addUserDocument.document
            })
          }
        })
        this.toggleAddDocumentForm(false)
        this.$success()
      } finally {
        this.loading = false
      }
    },
    async toggleDocumentDone(doc, isDone) {
      doc.isDone = isDone

      await this.$apollo.mutate({
        mutation: this.$withFragments(
          this.$options.mutation.SetDocumentDone,
          this.$options.fragments.CompletedAtDocumentFields,
          this.$options.fragments.IndicatorFields,
          this.$options.fragments.ProjectFields,
          this.$options.fragments.UpdatedAtDocumentFields
        ),
        variables: {
          indicatorId: this.indicator.id,
          documentId: doc.id,
          language: i18n.contentLanguage(),
          isDone: doc.isDone
        },
        update: this.afterUpdateDocument
      })
      this.$success()
    },
    async setDocumentNotes(doc, notes) {
      doc.notes = notes
      await this.$apollo.mutate({
        mutation: this.$withFragments(
          this.$options.mutation.SetDocumentNotes,
          this.$options.fragments.IndicatorFields,
          this.$options.fragments.ProjectFields,
          this.$options.fragments.UpdatedAtDocumentFields
        ),
        variables: {
          indicatorId: this.indicator.id,
          documentId: doc.id,
          language: i18n.contentLanguage(),
          notes: doc.notes
        },
        update: this.afterUpdateDocument
      })
      this.$success()
    },
    async updateDocument(doc, updatedDoc) {
      doc.link = updatedDoc.link
      doc.title = updatedDoc.title
      doc.content = updatedDoc.content

      const input = {
        indicatorId: this.indicator.id,
        documentId: doc.id,
        language: i18n.contentLanguage(),
        link: { value: doc.link },
        title: { value: updatedDoc.title },
        content: { value: updatedDoc.content }
      }

      await this.$apollo.mutate({
        mutation: this.$withFragments(
          this.$options.mutation.UpdateDocument,
          this.$options.fragments.IndicatorFields,
          this.$options.fragments.ProjectFields,
          this.$options.fragments.UpdatedAtDocumentFields
        ),
        variables: {
          input
        },
        update: this.afterUpdateDocument
      })
      this.$success()

      doc.__editing = false
    },
    afterUpdateDocument(cache, { data }) {
      this.$emit('documentsMutated', {
        cache: cache,
        action: 'updated',
        data: data.updateDocument.document
      })
    },
    async deleteDocument(doc) {
      await this.$apollo.mutate({
        mutation: this.$withFragments(
          this.$options.mutation.DeleteIndicatorDocument,
          this.$options.fragments.IndicatorFields,
          this.$options.fragments.ProjectFields
        ),
        variables: {
          input: {
            indicatorId: this.indicator.id,
            language: i18n.contentLanguage(),
            documentId: doc.id
          }
        },
        update: (cache) => {
          this.$emit('documentsMutated', {
            cache,
            action: 'deleted',
            data: doc
          })
        }
      })
    },
    async handleSortDocument({ moved }) {
      if (!moved) return

      // Compute new sorted list for optimistic response
      const sortedDocuments = this.doneDocs.concat() // copy
      const oldIndex = this.doneDocs.findIndex((x) => x.id == moved.element.id)
      const newIndex = moved.newIndex
      sortedDocuments.splice(newIndex, 0, sortedDocuments.splice(oldIndex, 1)[0]) // move item to new position – see: https://stackoverflow.com/a/5306832

      await this.$apollo.mutate({
        mutation: this.$withFragments(
          this.$options.mutation.SortDocument,
          this.$options.fragments.IndicatorFields,
          this.$options.fragments.ProjectFields
        ),
        variables: {
          indicatorId: this.indicator.id,
          language: i18n.contentLanguage(),
          documentId: moved.element.id,
          newIndex: moved.newIndex
        },
        optimisticResponse: {
          sortDocument: {
            __typename: 'SortDocumentPayload',
            sortedDocuments: sortedDocuments.map((x, i) => ({
              __typename: 'DocumentGraphType',
              id: x.id,
              sortOrder: i
            }))
          }
        }
      })

      this.$success()
    }
  }
}
</script>

<graphql>
mutation AddUserDocument($input: AddUserDocumentInput!) {
  addUserDocument(input: $input) {
    document {
      id
      ...DocumentListFields
      ...UpdatedAtDocumentFields
    }
    indicator {
      ...IndicatorFields
    }
    project {
      ...ProjectFields
    }
  }
}

mutation SetDocumentDone($indicatorId: ID!, $documentId: ID!, $language: ContentLanguage!, $isDone: Boolean!) {
  updateDocument(input: { indicatorId: $indicatorId, documentId: $documentId, language: $language, isDone: { value: $isDone } }) {
    document {
      id
      isDone
      sortOrder
      ...CompletedAtDocumentFields
      ...UpdatedAtDocumentFields
    }
    indicator {
      ...IndicatorFields
    }
    project {
      ...ProjectFields
    }
  }
}

mutation SetDocumentNotes($indicatorId: ID!, $documentId: ID!, $language: ContentLanguage!, $notes: String) {
  updateDocument(input: { indicatorId: $indicatorId, documentId: $documentId, language: $language, notes: { value: $notes } }) {
    document {
      id
      notes
      ...UpdatedAtDocumentFields
    }
    indicator {
      ...IndicatorFields
    }
    project {
      ...ProjectFields
    }
  }
}

mutation UpdateDocument($input: UpdateDocumentInput!) {
  updateDocument(input: $input) {
    document {
      id
      title
      link
      content
      contentHtml
      sortOrder
      ...UpdatedAtDocumentFields
    }
    indicator {
      ...IndicatorFields
    }
    project {
      ...ProjectFields
    }
  }
}

mutation DeleteIndicatorDocument($input: DeleteIndicatorDocumentInput!) {
  deleteIndicatorDocument(input: $input) {
    indicator {
      ...IndicatorFields
    }
    project {
      ...ProjectFields
    }
  }
}

mutation SortDocument($indicatorId: ID!, $language: ContentLanguage!, $documentId: ID!, $newIndex: Int!) {
  sortDocument(input: { indicatorId: $indicatorId, language: $language, documentId: $documentId, newIndex: $newIndex }) {
    sortedDocuments {
      id
      sortOrder
    }
    indicator {
      ...IndicatorFields
    }
    project {
      ...ProjectFields
    }
  }
}
</graphql>

<fragments>
fragment DocumentListFields on DocumentGraphType {
  id
  source
  title
  link
  content
  contentHtml
  isDone
  notes
  sortOrder
}

fragment CompletedAtDocumentFields on DocumentGraphType {
  completedAt
  completedBy {
    id
    profile {
      id
      firstName
      lastName
    }
  }
}

fragment IndicatorFields on IndicatorGraphType {
  id
  language
  updatedAt
  updatedBy {
    id
    profile {
      id
      firstName
      lastName
    }
  }
}

fragment ProjectFields on ProjectGraphType {
  id
  updatedAt
  updatedBy {
    id
    profile {
      id
      firstName
      lastName
    }
  }
}

fragment UpdatedAtDocumentFields on DocumentGraphType {
  updatedAt
  updatedBy {
    id
    profile {
      id
      firstName
      lastName
    }
  }
}
</fragments>

<style lang="scss" scoped>
// Transition group
.documents-move {
  transition: transform 0.5s;
}
.documents-leave-active {
  transition: opacity 0.5s;
}
.documents-leave-to {
  opacity: 0;
}
</style>
